import { defineComponent as _defineComponent } from 'vue'
import {ref, watch, onMounted, Ref} from 'vue';
import { useTranslations } from "@/hooks/useTranslations";
import VModal from "@/components/vModal.vue";
import {AdditionalCost} from "@/models/AdditionalCost/AdditionalCost";
import {Vendor} from "@/models/IngredientCost/Vendor";
import {IngredientCostMeasurement} from "@/models/IngredientCost/IngredientCostMeasurement";
import IngredientCostRepository from "@/utils/API/IngredientCostRepository";
import {getErrorMessage} from "@/utils/Helpers";
import {useNotify} from "@/hooks/useNotify";
import MeasurementRepository from "@/utils/API/MeasurementRepository";
import {MeasurementCategory} from "@/models/Measurement/MeasurementCategory";
import AdditionalCostRepository from "@/utils/API/AdditionalCostRepository";
import {ModifyIngredientCostDto} from "@/models/RecipeInvoice/ModifyIngredientCostDto";
import VAddVendorModal from "@/components/costing/vAddVendorModal.vue";
import VAdditionalMeasurementModal from "@/components/costing/vAdditionalMeasurementModal.vue";
import {IngredientCost} from "@/models/IngredientCost/IngredientCost";

type vModifyAdditionalCostProps = {
  showAdditionalCostModal: boolean;
  additionalCost: AdditionalCost;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'vModifyAdditionalCost',
  props: {
    showAdditionalCostModal: { type: Boolean },
    additionalCost: null
  },
  emits: ['on-cancel', 'save'],
  setup(__props: any, { emit }) {

const props = __props as {
  showAdditionalCostModal: boolean;
  additionalCost: AdditionalCost;
};


const additionalCostForm = ref(null);

const translations = useTranslations();
const localAdditionalCost = ref<AdditionalCost>({
  id: 0,
  name: '',
  cost: null,
  restaurantId: 0,
  vendor: { id: 0, name: '' } as Vendor,
  quantity: null,
  ingredientCostMeasurement: {
    measurement: {
      conversionCoeficient: 0,
      isDefault: false,
      measurementCategory: MeasurementCategory.Mass,
      name: "",
      id: 0,
    },
    weightInGrams: 0,
    measurementId: 0,
    ingredientCostId: 0,
    createdOn: null,
    id: 0
  },
  ingredientCostMeasurements: [],
});

const vendors = ref<any[]>([]);
const notify = useNotify();
const showAddingVendorModal = ref(false);
const showAddingMeasurementrModal = ref(false);

watch(
  () => props.additionalCost,
  (newVal) => {
    localAdditionalCost.value = newVal ? { ...newVal } : { ...localAdditionalCost.value };
  },
  { immediate: true }
);

function closeModal() {
  emit("on-cancel");
}

onMounted(() => {
  loadVendors();
});

async function saveAdditionalCost() {
  const isEdit = Boolean(props.additionalCost.id);

  try {
    if (isEdit) {
      const dto = toModifyDto(localAdditionalCost.value);
      await AdditionalCostRepository.updateAdditionalCost(dto);
    }
    else {
      const dtoArray = [toCreateDto(localAdditionalCost.value)];
      await AdditionalCostRepository.createAdditionalCost(dtoArray);
    }

    emit("save", localAdditionalCost.value);
    notify.success(translations.value.get("ms_save_success"), { closeOnClick: true, timeout: 3500 });
    closeModal();
  }
  catch (error) {
    notify.error("Please fix the errors.", { closeOnClick: true, timeout: 3500 });
  }
}

function toCreateDto(cost: AdditionalCost) {
  return {
    name: cost.name,
    vendorId: cost.vendor.id,
    cost: cost.cost,
    quantity: cost.quantity,
    chosenMeasurementName: cost.ingredientCostMeasurement.measurement.name || "",
    ingredientCostMeasurements: cost.ingredientCostMeasurements.map((m) => ({
      id: m.id || 0,
      ingredientCostId: cost.id || 0,
      measurementId: m.measurement.id || 0,
      name: m.measurement.name || "",
      weightInGrams: m.weightInGrams
    }))
  };
}

function toModifyDto(cost: AdditionalCost) {
  return {
    id: cost.id,
    name: cost.name,
    vendorId: cost.vendor.id,
    cost: cost.cost,
    quantity: cost.quantity,
    chosenMeasurementName: String(
      cost.ingredientCostMeasurement.measurement.name || ""
    ),
    ingredientCostMeasurements: cost.ingredientCostMeasurements.map((m) => ({
      id: m.id || 0,
      ingredientCostId: cost.id || 0,
      measurementId: m.measurement.id || 0,
      name: m.measurement.name || "",
      weightInGrams: m.weightInGrams
    }))
  };
}


function openAddVendorModal() {
  showAddingVendorModal.value = true;
}

function handleVendorAdded(vendorList: Vendor[]) {
  vendors.value = vendorList;
}

function openAddMeasurementModal() {
  showAddingMeasurementrModal.value = true;
}

function loadVendors() {
  if (!vendors.value.length) {
    IngredientCostRepository.listIngredientCostVendors()
      .then((res) => vendors.value = res.data.result)
      .catch((err) => notify.error(getErrorMessage(err, translations)));
  }
}

function handleSaveMeasurements(newMeasurements: any[]) {
  let measurementNames = localAdditionalCost.value.ingredientCostMeasurements.map(
    (x) => x.measurement.name
  );

  newMeasurements.map((x) => {
    if (!measurementNames.includes(x.name)) {
      localAdditionalCost.value.ingredientCostMeasurements.push({
        measurement: {
          conversionCoeficient: 0,
          isDefault: false,
          measurementCategory: MeasurementCategory.Mass,
          name: x.name,
          id: 0,
        },
        weightInGrams: x.weight,
        measurementId: 0,
        ingredientCostId: 0,
        createdOn: null,
        id: 0
      });
    }
    return x;
  });

  showAddingMeasurementrModal.value = false;
}

function onNumberInput(event: Event, field: 'quantity' | 'cost') {
  const target = event.target as HTMLInputElement;
  let value = target.value.replace(/[^0-9.]/g, '');
  value = value.replace(/(\..*)\./g, '$1');
  localAdditionalCost.value[field] = value ? parseFloat(value) : null;
}


return { __sfc: true,props, additionalCostForm, emit, translations, localAdditionalCost, vendors, notify, showAddingVendorModal, showAddingMeasurementrModal, closeModal, saveAdditionalCost, toCreateDto, toModifyDto, openAddVendorModal, handleVendorAdded, openAddMeasurementModal, loadVendors, handleSaveMeasurements, onNumberInput, VModal, VAddVendorModal, VAdditionalMeasurementModal }
}

})